// The §2.22 SALES SETTINGS data layer (P3-E PR-E2, D-292 ruling 11): the
// `/sales/settings` screen's read and its full-row PUT.
//
// The GET rides `sales.read`; the PUT rides `sales.tier.manage` (§5). The row
// carries two READ-ONLY stamps since PR-E2 — `engine_swept_at` and
// `reminders_active_since` — which the PUT's strict schema refuses, so the
// request body is built field by field from the form (`settingsPutBody`) and
// never by spreading the read.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { newIdempotencyKey, unwrap } from "../../../../lib/request";

type S = components["schemas"];

export type SalesSettings = S["SalesSettings"];
export type SalesSettingsPutRequest = S["SalesSettingsPutRequest"];
export type ReminderTierOverrides = S["ReminderTierOverrides"];

export const salesSettingsKey = ["sales", "settings"] as const;

export function useSalesSettings(enabled: boolean) {
  return useQuery({
    queryKey: salesSettingsKey,
    enabled,
    queryFn: async ({ signal }) =>
      unwrap(await client.GET("/v1/sales-settings", { signal }), "Could not load the settings"),
  });
}

/** `PUT /v1/sales-settings` — the answer is the stored row, which replaces the
 * cache so the form re-seeds from what the server kept, not what was typed. */
export function useUpdateSalesSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SalesSettingsPutRequest) =>
      unwrap(
        await client.PUT("/v1/sales-settings", {
          params: { header: { "Idempotency-Key": newIdempotencyKey() } },
          body,
        }),
        "Could not save the settings",
      ),
    onSuccess: (row) => {
      qc.setQueryData(salesSettingsKey, row);
    },
  });
}
